import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Trash2, Loader2, Pencil, Check, X } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function TrustedDevices({ refreshTick = 0 }) {
  const [data, setData] = useState({ trusted: [], attempts: [] });
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/auth/devices")
      .then((d) => setData({ trusted: d.trusted || [], attempts: d.attempts || [] }))
      .catch((e) => logger.error("devices load failed:", e))
      .finally(() => setLoading(false));
  };
  useEffect(load, [refreshTick]);

  const trust = async (id) => { await api.post("/auth/devices/trust", { id }); load(); };
  const remove = async (id) => { await api.post("/auth/devices/remove", { id }); load(); };

  const startEdit = (t) => { setEditId(t.id); setDraft(t.label || ""); };
  const cancelEdit = () => { setEditId(null); setDraft(""); };
  const saveEdit = async (id) => {
    const label = draft.trim();
    if (!label) return;
    setSaving(true);
    try {
      await api.post("/auth/devices/rename", { id, label });
      setData((d) => ({ ...d, trusted: d.trusted.map((t) => (t.id === id ? { ...t, label } : t)) }));
      cancelEdit();
      // no local toast: the security.device_renamed WS broadcast confirms it for all admins
    } catch (e) { logger.error("rename failed:", e); toast.error("Couldn't rename device. Try again."); }
    finally { setSaving(false); }
  };

  if (loading) return <p className="flex items-center gap-2 py-10 justify-center text-slate-400 text-sm" data-testid="devices-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>;

  return (
    <div className="space-y-6" data-testid="trusted-devices">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <p className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-800 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Trusted Devices ({data.trusted.length})</p>
        {data.trusted.length === 0 ? <p className="px-5 py-6 text-center text-xs text-slate-400 italic">No trusted devices yet.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-[11px] uppercase text-slate-400 text-left"><th className="px-5 py-2">User</th><th className="px-3 py-2">Device</th><th className="px-3 py-2">Last Login</th><th className="px-3 py-2">IP</th><th className="px-3 py-2"></th></tr></thead>
            <tbody>
              {data.trusted.map((t) => (
                <tr key={t.id} className="border-t border-slate-50" data-testid={`trusted-row-${t.id}`}>
                  <td className="px-5 py-2 font-medium">{t.username}</td>
                  <td className="px-3 py-2 text-slate-500">
                    {editId === t.id ? (
                      <div className="flex items-center gap-1.5">
                        <Input
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") saveEdit(t.id); if (e.key === "Escape") cancelEdit(); }}
                          maxLength={60}
                          autoFocus
                          placeholder="e.g. Priya's Office PC"
                          className="h-7 text-xs w-48"
                          data-testid={`device-name-input-${t.id}`}
                        />
                        <Button size="icon" className="h-7 w-7 bg-emerald-600 hover:bg-emerald-700" disabled={saving || !draft.trim()} onClick={() => saveEdit(t.id)} data-testid={`save-device-name-${t.id}`}>{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}</Button>
                        <Button size="icon" variant="outline" className="h-7 w-7" onClick={cancelEdit} data-testid={`cancel-device-name-${t.id}`}><X className="h-3.5 w-3.5" /></Button>
                      </div>
                    ) : (
                      <span className="group inline-flex items-center gap-1.5">
                        {t.label || "—"}
                        <button className="opacity-40 hover:opacity-100 focus-visible:opacity-100 transition-opacity text-slate-400 hover:text-slate-700" onClick={() => startEdit(t)} data-testid={`rename-device-${t.id}`} title="Rename device"><Pencil className="h-3 w-3" /></button>
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-500">{fmt(t.last_login)}</td>
                  <td className="px-3 py-2 font-mono-data text-xs text-slate-500">{t.last_ip || "—"}</td>
                  <td className="px-3 py-2 text-right"><Button size="sm" variant="outline" className="h-7 text-[11px] text-rose-600 border-rose-200" onClick={() => remove(t.id)} data-testid={`remove-device-${t.id}`}><Trash2 className="h-3 w-3 mr-1" /> Remove</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <p className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-800 flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-amber-600" /> Unauthorised Access Log ({data.attempts.length})</p>
        {data.attempts.length === 0 ? <p className="px-5 py-6 text-center text-xs text-slate-400 italic" data-testid="attempts-empty">No unauthorised login attempts.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-[11px] uppercase text-slate-400 text-left"><th className="px-5 py-2">User</th><th className="px-3 py-2">IP</th><th className="px-3 py-2">When</th><th className="px-3 py-2">Device</th><th className="px-3 py-2"></th></tr></thead>
            <tbody>
              {data.attempts.map((a) => (
                <tr key={a.id} className={`border-t border-slate-50 ${a.repeated ? "bg-red-50" : ""}`} data-testid={`attempt-row-${a.id}`}>
                  <td className="px-5 py-2 font-medium">{a.username}</td>
                  <td className={`px-3 py-2 font-mono-data text-xs ${a.repeated ? "text-red-600 font-semibold" : "text-slate-500"}`}>{a.ip || "—"}{a.repeated && " ⚠"}</td>
                  <td className="px-3 py-2 text-slate-500">{fmt(a.at)}</td>
                  <td className="px-3 py-2 text-slate-500">{a.device_details || "—"}</td>
                  <td className="px-3 py-2 text-right"><Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700" onClick={() => trust(a.id)} data-testid={`trust-device-${a.id}`}><ShieldCheck className="h-3 w-3 mr-1" /> Trust This Device</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
